import React, { useState, useEffect } from 'react';
import './GameMaker.css';

/**
 * FastTravelNavigation Component
 * 
 * Provides quick navigation to different sections of the GameMaker interface.
 * Shows floating navigation buttons on the side for easy section jumping.
 * Responsive design that works well on both desktop and mobile.
 */
const FastTravelNavigation = ({ activeTab }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [visibleSections, setVisibleSections] = useState([]);

  // Define section configurations with icons, colors, and scroll targets
  const sectionConfigs = {
    // PersonalizedSection sections (dynamic - only show if they exist and have content)
    'games-waiting': {
      id: 'games-waiting',
      title: 'Waiting for You',
      shortTitle: 'Your Turn',
      icon: '⏰',
      color: '#ef4444', // red for urgent
      priority: 1,
      category: 'personalized'
    },
    'draw-offers': {
      id: 'draw-offers', 
      title: 'Your Draw Offers',
      shortTitle: 'Draw Offers',
      icon: '🤝',
      color: '#ef4444', // red for urgent
      priority: 2,
      category: 'personalized'
    },
    'active-games': {
      id: 'active-games',
      title: 'Your Active Games', 
      shortTitle: 'Active Games',
      icon: '🎮',
      color: '#f59e0b', // orange for medium priority
      priority: 3,
      category: 'personalized'
    },
    'personal-offers': {
      id: 'personal-offers',
      title: 'Personal Offers',
      shortTitle: 'For You', 
      icon: '🎯',
      color: '#ef4444', // red for urgent
      priority: 4,
      category: 'personalized'
    },
    'your-offers': {
      id: 'your-offers',
      title: 'Your Active Offers',
      shortTitle: 'Your Offers',
      icon: '📋',
      color: '#f59e0b', // orange for medium priority  
      priority: 5,
      category: 'personalized'
    },
    'completed-games': {
      id: 'completed-games',
      title: 'Your Completed Games',
      shortTitle: 'Completed', 
      icon: '🏆',
      color: '#6b7280', // gray for low priority
      priority: 6,
      category: 'personalized'
    },

    // GeneralSection sections (always visible)
    'create-offer': {
      id: 'create-offer',
      title: 'Create New Offer',
      shortTitle: 'Create',
      icon: '➕',
      color: '#3b82f6', // blue for info
      priority: 7,
      category: 'general'
    },
    'browse-offers': {
      id: 'browse-offers', 
      title: 'Browse Offers',
      shortTitle: 'Offers',
      icon: '🔍',
      color: '#8b5cf6', // purple
      priority: 8,
      category: 'general'
    },
    'browse-games': {
      id: 'browse-games',
      title: 'Browse Games',
      shortTitle: 'Games', 
      icon: '🎲',
      color: '#f97316', // light red
      priority: 9,
      category: 'general'
    }
  };

  // Scan for visible sections on mount and when content changes
  useEffect(() => {
    const scanVisibleSections = () => {
      const visible = [];
      
      Object.values(sectionConfigs).forEach(config => {
        // For PersonalizedSection, check if the collapsible box exists and has content
        if (config.category === 'personalized') {
          const element = document.querySelector(`[data-section-id="${config.id}"]`);
          if (element && !element.querySelector('.empty-section')) {
            visible.push(config);
          }
        }
        // GeneralSection items are always visible when on oneOnOne tab
        else if (config.category === 'general' && activeTab === 'oneOnOne') {
          visible.push(config);
        }
      });

      // Sort by priority
      visible.sort((a, b) => a.priority - b.priority);
      setVisibleSections(visible);
    };

    // Initial scan
    scanVisibleSections();
    
    // Scan periodically for dynamic content changes
    const interval = setInterval(scanVisibleSections, 2000);
    return () => clearInterval(interval);
  }, [activeTab]);

  // Handle section navigation
  const navigateToSection = (sectionId) => {
    const element = document.querySelector(`[data-section-id="${sectionId}"]`);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start',
        inline: 'nearest'
      });
      
      // Expand the section if it's collapsed
      const collapsibleHeader = element.querySelector('.collapsible-header');
      const isCollapsed = element.classList.contains('collapsed');
      if (isCollapsed && collapsibleHeader) {
        collapsibleHeader.click();
      }
    }
    
    // Auto-collapse on mobile after navigation
    if (window.innerWidth < 768) {
      setIsExpanded(false);
    }
  };

  // Don't show navigation if no sections are visible or not on relevant tabs
  if (visibleSections.length === 0 || (activeTab !== 'oneOnOne' && activeTab !== 'asymmetric')) {
    return null;
  }

  return (
    <div className="fast-travel-nav">
      {/* Toggle Button */}
      <button 
        className={`fast-travel-toggle ${isExpanded ? 'expanded' : ''}`}
        onClick={() => setIsExpanded(!isExpanded)}
        title="Quick Navigation"
      >
        <span className="toggle-icon">
          {isExpanded ? '✕' : '📋'}
        </span>
        <span className="toggle-text">
          {isExpanded ? 'Close' : 'Nav'}  
        </span>
      </button>

      {/* Navigation Panel */}
      {isExpanded && (
        <>
          {/* Backdrop for mobile */}
          <div 
            className="fast-travel-backdrop md:hidden"
            onClick={() => setIsExpanded(false)}
          />
          
          {/* Navigation Panel */}
          <div className="fast-travel-panel">
            <div className="panel-header">
              <h3 className="panel-title">Quick Navigation</h3>
              <button 
                className="panel-close md:hidden"
                onClick={() => setIsExpanded(false)}
              >
                ✕
              </button>
            </div>
            
            <div className="section-list">
              {visibleSections.map(section => (
                <button
                  key={section.id}
                  className="section-nav-item"
                  style={{ 
                    borderLeftColor: section.color,
                    color: section.color 
                  }}
                  onClick={() => navigateToSection(section.id)}
                  title={section.title}
                >
                  <span className="section-icon">{section.icon}</span>
                  <span className="section-name">
                    {section.title}
                  </span>
                </button>
              ))}
            </div>

            {/* Category Separators */}
            {visibleSections.some(s => s.category === 'personalized') && 
             visibleSections.some(s => s.category === 'general') && (
              <div className="category-separator">
                <span className="separator-line"></span>
                <span className="separator-text">General</span>
                <span className="separator-line"></span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default FastTravelNavigation;